// Concentric arcs. A section sits on circles around one focal point, which is
// its block origin. Angles are in degrees and 0 points down the map, away from
// the stage; positive angles go right.
//
// An edge is { deg, px }: a fixed angle plus a fixed walking distance along the
// row. `px` is what keeps an aisle the same width from front to back, because
// the same distance is a smaller angle on a longer row.
const toRadians = (deg) => (deg * Math.PI) / 180;
const toDegrees = (rad) => (rad * 180) / Math.PI;
const FLOAT_SLACK = 1e-9;

export const edgeAngle = (edge, radius) =>
  edge.deg + toDegrees(edge.px / radius);

// Seats that fit between two edges at this radius. Rounding down means seats
// spread to meet both edges, so they are never closer than the pitch.
export function arcRowCount(radius, from, to, pitch) {
  const span = toRadians(edgeAngle(to, radius) - edgeAngle(from, radius));
  return span > 0 ? Math.floor((radius * span) / pitch + FLOAT_SLACK) + 1 : 1;
}

// `rowSeats` holds the rows to their counts: one for every row, as arcs drawn
// before concentric rows have, or one per row, as a change of curve or spacing
// leaves them, so the same seats stay on the map. A held row sits about its
// middle, `rowPitch` apart along the arc (seat spacing if not given).
export function arcSeats({
  radius,
  rows,
  rowSpacing,
  seatSpacing,
  from,
  to,
  rowSeats,
  rowPitch,
}) {
  const seats = [];
  for (let row = 0; row < rows; row++) {
    const R = radius + row * rowSpacing;
    const start = edgeAngle(from, R),
      span = edgeAngle(to, R) - start;
    const held = Array.isArray(rowSeats) ? rowSeats[row] : 0;
    const count = held || rowSeats || arcRowCount(R, from, to, seatSpacing);
    // A held row keeps its spacing about the middle, so it neither crowds nor
    // gaps when the curve or spacing changes.
    const step = heldStep(R, rowPitch?.[row] ?? seatSpacing);
    for (let index = 0; index < count; index++) {
      const a = toRadians(
        held
          ? start + span / 2 + (index - (count - 1) / 2) * step
          : start + span * (count === 1 ? 0.5 : index / (count - 1)),
      );
      seats.push({
        row,
        index,
        count,
        x: R * Math.sin(a),
        y: R * Math.cos(a),
      });
    }
  }
  return seats;
}

// Degrees between two seats `pitch` apart along a row at radius R.
export const heldStep = (R, pitch) => toDegrees(pitch / R);

export const arcMiddle = (from, to, radius) =>
  (edgeAngle(from, radius) + edgeAngle(to, radius)) / 2;

// The edges that give the front row `columns` seats, holding the centre line.
export function fitArcEdges(from, to, radius, pitch, columns) {
  const width = (columns - 1) * pitch;
  const centre = (from.deg + to.deg) / 2,
    half = toDegrees((width - (to.px - from.px)) / radius) / 2;
  return {
    from: { ...from, deg: centre - half },
    to: { ...to, deg: centre + half },
  };
}

// Moves one edge by a number of front-row seats.
export const shiftArcEdge = (edge, seats, pitch, radius) => ({
  ...edge,
  deg: edge.deg + toDegrees((seats * pitch) / radius),
});

export const curvedEdges = (radius, pitch, columns, aim = 0) => {
  const half = toDegrees(((columns - 1) * pitch) / radius) / 2;
  return {
    from: { deg: aim - half, px: 0 },
    to: { deg: aim + half, px: 0 },
  };
};

// Curve is the front row's sweep: 0% is straight and 100% as far as the section
// goes, which is a half circle unless the row is too short to bend that far.
export const MIN_ARC_RADIUS = 40;
const fullSweep = (width) => Math.min(Math.PI, width / MIN_ARC_RADIUS);
export function curveOf(columns, pitch, radius) {
  const width = (columns - 1) * pitch;
  return width > 0
    ? Math.min(100, Math.round((width / radius / fullSweep(width)) * 100))
    : 0;
}
export function radiusForCurve(columns, pitch, percent) {
  const width = (columns - 1) * pitch;
  return width > 0
    ? Math.max(MIN_ARC_RADIUS, width / (fullSweep(width) * (percent / 100)))
    : MIN_ARC_RADIUS;
}
